import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Clock, MapPin, ChevronLeft, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED = '#E63946';
const BLUE = '#2563EB';
const GREEN = '#22C55E';

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  let h = d.getHours(); const m = String(d.getMinutes()).padStart(2, '0');
  const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
  return `${h}:${m} ${ap}`;
}
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function SportsPublicMatchPage({ subdomainSlug }) {
  const params = useParams();
  const slug = subdomainSlug || params.slug;
  const matchId = params.matchId;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sports/public/${slug}/match/${matchId}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Match not found');
    }
  }, [slug, matchId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000); // live refresh in case it's in progress
    return () => clearInterval(id);
  }, [load]);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#0A0A0A', color: '#fff' }}>
      <p className="text-lg font-bold" data-testid="match-history-error">{error}</p>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A', color: '#737373' }}>Loading match…</div>;

  const { facility, tournament, match, timeline } = data;
  const aWon = match.winner_side === 'a';
  const bWon = match.winner_side === 'b';
  const finished = match.status === 'completed' || match.status === 'bye';
  const live = match.status === 'live';

  const TeamHead = ({ name, won, color, gw }) => (
    <div className="flex-1 text-center">
      <p className="text-base sm:text-xl font-black uppercase tracking-wide truncate px-1" style={{ color, fontFamily: 'Oswald, sans-serif' }}>{name || 'TBD'}</p>
      <p className="font-black tabular-nums leading-none mt-1" style={{ color: '#fff', fontSize: '56px' }}>{gw}</p>
      {won && <span className="inline-flex items-center gap-1 mt-1 text-xs font-bold" style={{ color: GREEN }}><Trophy className="h-3.5 w-3.5" /> WINNER</span>}
    </div>
  );

  const bracketLabel = { winners: 'Winners Bracket', losers: 'Losers Bracket', final: 'Grand Final', final_reset: 'Grand Final (Reset)', pool: 'Pool Play' }[match.bracket_type] || '';

  return (
    <div className="min-h-screen pb-10" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Inter, sans-serif' }} data-testid="public-match-history">
      {/* Header */}
      <div className="px-4 py-4" style={{ background: '#000', borderBottom: '1px solid #1A1A1A' }}>
        <a href={`/book/${slug}/tournament/${tournament.id}`} className="inline-flex items-center gap-1 text-xs mb-2" style={{ color: '#737373' }}>
          <ChevronLeft className="h-4 w-4" /> {tournament.name}
        </a>
        <p className="text-xs uppercase tracking-widest" style={{ color: '#525252' }}>{facility.name}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <h1 className="text-lg font-black" style={{ fontFamily: 'Oswald, sans-serif' }}>Round {match.round} · Match {match.match_number}</h1>
          {bracketLabel && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#1A1A1A', color: '#A3A3A3' }}>{bracketLabel}</span>}
          {match.division_name && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${RED}22`, color: RED }}>{match.division_name}</span>}
        </div>
        <div className="flex items-center gap-3 mt-1.5 text-xs" style={{ color: '#737373' }}>
          {live && <span className="inline-flex items-center gap-1 font-bold" style={{ color: RED }}><Activity className="h-3.5 w-3.5 animate-pulse" /> LIVE</span>}
          {finished && <span className="inline-flex items-center gap-1 font-bold" style={{ color: GREEN }}>FINAL</span>}
          {match.court_name && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {match.court_name}</span>}
          {match.completed_at && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(match.completed_at)} {fmtTime(match.completed_at)}</span>}
        </div>
      </div>

      {/* Score header */}
      <div className="px-4 py-6">
        <div className="flex items-center justify-center gap-3 rounded-2xl py-5" style={{ background: '#0F0F0F', border: '1px solid #1A1A1A' }}>
          <TeamHead name={match.team_a_name} won={aWon} color={RED} gw={match.games_won_a} />
          <div className="text-center px-2">
            <p className="text-2xl font-black" style={{ color: '#525252' }}>VS</p>
            <p className="text-[10px] mt-1" style={{ color: '#525252' }}>Best of {match.best_of}</p>
            <p className="text-[10px]" style={{ color: '#525252' }}>to {match.scoring_target || '∞'}</p>
          </div>
          <TeamHead name={match.team_b_name} won={bWon} color={BLUE} gw={match.games_won_b} />
        </div>
      </div>

      {/* Game-by-game breakdown */}
      <div className="px-4">
        <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#737373' }}>Game Breakdown</p>
        <div className="space-y-2" data-testid="match-games-list">
          {(match.games || []).map((g, i) => {
            const gw = g.winner; // 'a'|'b'
            return (
              <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: '#0F0F0F', border: '1px solid #1A1A1A' }}>
                <span className="text-xs font-black px-2 py-1 rounded" style={{ background: '#1A1A1A', color: '#A3A3A3' }}>GAME {g.game_number}</span>
                <div className="flex-1 flex items-center justify-center gap-3">
                  <span className="text-2xl font-black tabular-nums" style={{ color: gw === 'a' ? GREEN : '#737373' }}>{g.score_a}</span>
                  <span className="text-sm" style={{ color: '#525252' }}>–</span>
                  <span className="text-2xl font-black tabular-nums" style={{ color: gw === 'b' ? GREEN : '#737373' }}>{g.score_b}</span>
                </div>
                <span className="text-[10px] font-bold uppercase w-20 text-right truncate" style={{ color: gw === 'a' ? RED : BLUE }}>
                  {gw === 'a' ? match.team_a_name : match.team_b_name}
                </span>
              </div>
            );
          })}
          {live && (
            <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: `${RED}0D`, border: `1px solid ${RED}33` }}>
              <span className="text-xs font-black px-2 py-1 rounded" style={{ background: RED, color: '#fff' }}>GAME {match.current_game}</span>
              <div className="flex-1 flex items-center justify-center gap-3">
                <span className="text-2xl font-black tabular-nums" style={{ color: RED }}>{match.score_a}</span>
                <span className="text-sm" style={{ color: '#525252' }}>–</span>
                <span className="text-2xl font-black tabular-nums" style={{ color: BLUE }}>{match.score_b}</span>
              </div>
              <span className="text-[10px] font-bold uppercase w-20 text-right" style={{ color: RED }}>in progress</span>
            </div>
          )}
          {(match.games || []).length === 0 && !live && (
            <p className="text-xs text-center py-4" style={{ color: '#525252' }}>No games recorded yet.</p>
          )}
        </div>
      </div>

      {/* Point-by-point timeline (two columns) */}
      {timeline && timeline.filter(e => e.action === 'point').length > 0 && (
        <div className="px-4 mt-6">
          <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#737373' }}>Point-by-Point</p>
          <div className="grid grid-cols-2 gap-2" data-testid="match-timeline-columns">
            {[{ side: 'a', name: match.team_a_name, color: RED }, { side: 'b', name: match.team_b_name, color: BLUE }].map(({ side, name, color }) => {
              const pts = timeline.filter(e => e.action === 'point' && e.team === side);
              return (
                <div key={side}>
                  <div className="text-center text-xs font-black uppercase py-1.5 rounded-t-lg truncate px-1" style={{ background: color, color: '#fff' }}>{name}</div>
                  <div className="space-y-1 mt-1">
                    {pts.length === 0 && <p className="text-[10px] text-center py-2" style={{ color: '#525252' }}>—</p>}
                    {pts.map((e, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded" style={{ background: `${color}14`, border: `1px solid ${color}33` }}>
                        <span className="font-black tabular-nums text-sm" style={{ color: '#fff' }}>{side === 'a' ? e.score_a_after : e.score_b_after}</span>
                        <span className="font-bold" style={{ color }}>G{e.game_number}</span>
                        <span className="text-[10px]" style={{ color: '#525252' }}>{fmtTime(e.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
